import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { nav } from "../../data/content";
import { Logo } from "./Logo";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // On non-home pages, always use light glass style
  const scrolledMod = scrolled ? "glass-nav--scrolled" : "";
  const glassClass = !isHome
    ? `glass-nav--light ${scrolledMod}`
    : scrolled
      ? `glass-nav--light ${scrolledMod}`
      : `glass-nav ${scrolledMod}`;

  const textClass = !isHome
    ? "text-brand-primary"
    : scrolled
      ? "text-brand-primary"
      : "text-white";

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
      className={`fixed top-0 inset-x-0 z-50 ${glassClass}`}
      data-testid="site-header"
    >
      <div className="max-w-[90rem] mx-auto px-6 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between">
        <Link
          to="/"
          className={`flex items-center gap-2.5 ${textClass}`}
          data-testid="nav-logo"
          aria-label="Metalotsen Startseite"
        >
          <Logo className="h-9 w-9" />
          <span className="font-semibold tracking-tight text-[17px]">
            Metalotsen
          </span>
        </Link>

        <nav
          className="hidden lg:flex items-center gap-1"
          aria-label="Hauptnavigation"
        >
          {nav.map((item) => (
            <a
              key={item.id}
              href={isHome ? item.href : `/${item.href}`}
              data-testid={`nav-link-${item.id}`}
              className={`px-4 py-2 text-[14px] font-medium tracking-tight ${textClass} opacity-80 hover:opacity-100 transition-opacity`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={isHome ? "#kontakt" : "/#kontakt"}
            data-testid="nav-cta"
            className="hidden sm:inline-flex items-center gap-2 pl-5 pr-4 py-2.5 rounded-full bg-brand-accent text-white text-[13px] font-medium hover:bg-brand-accentHover transition-colors"
          >
            Kostenloses Erstgespräch
            <ArrowUpRight className="h-4 w-4" />
          </a>

          <button
            type="button"
            aria-label="Menü öffnen"
            onClick={() => setOpen(true)}
            data-testid="nav-mobile-open"
            className={`lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border ${
              isHome && !scrolled
                ? "border-white/20 text-white"
                : "border-brand-border text-brand-primary"
            }`}
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden fixed inset-0 z-[60] bg-brand-primary text-white"
            data-testid="mobile-menu"
          >
            <div className="max-w-[90rem] mx-auto px-6 sm:px-8 h-[72px] flex items-center justify-between">
              <Link
                to="/"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5"
              >
                <Logo className="h-9 w-9" />
                <span className="font-semibold text-[17px]">Metalotsen</span>
              </Link>
              <button
                type="button"
                aria-label="Menü schließen"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-close"
                className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/20"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="px-6 sm:px-8 pt-6 pb-10 flex flex-col gap-2">
              {nav.map((item, i) => (
                <motion.a
                  key={item.id}
                  href={isHome ? item.href : `/${item.href}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                  className="py-4 border-b border-white/10 text-2xl font-medium tracking-tight"
                >
                  {item.label}
                </motion.a>
              ))}
              <a
                href={isHome ? "#kontakt" : "/#kontakt"}
                onClick={() => setOpen(false)}
                className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-accent text-white font-medium"
              >
                Kostenloses Erstgespräch
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
